<template>
  <div>
    <div class="card-header mb-3">{{ $t('title.general.user_settings.change_data') }}</div>
    <b-form @submit.prevent="updateUserData">

      <!--EMAIL-->
      <b-form-group
        id="email-group"
        :label="$t('form.user_settings.email')"
        :invalid-feedback="invalidEmail"
        :state="stateEmail"
        label-for="email"
      >
        <b-form-input
          id="email"
          :disabled="user.isSubaccount"
          v-model="email"
          :state="($v.email.$error) ? false : null"
          :placeholder="$t('form.user_settings.placeholder_email')"
        ></b-form-input>
      </b-form-group>

      <!--USERNAME-->
      <!--show it only for admins as customers cannot access to their usernames-->
      <b-form-group
        v-if="isAdminSettings"
        id="username-group"
        :label="$t('form.user_settings.username')"
        label-for="username"
        :invalid-feedback="invalidUsername"
        :state="stateUsername"
      >
        <b-form-input
          id="username"
          v-model="username"
          type="text"
          required
          :state="($v.username.$error) ? false : null"
          :placeholder="$t('form.user_settings.placeholder_username')"
        ></b-form-input>
      </b-form-group>

      <!--PHONE-->
      <b-form-group
        v-if="showPhone"
        id="phone-group"
        :label="$t('form.customer.phone')"
        label-for="phone"
      >
        <vue-phone-number-input
          id="phone"
          v-model="phoneNumberData.formattedNumber"
          :error="hasInvalidPhoneNumber"
          v-bind="userMarket.isFlealoverSoloFeature ? {} : { onlyCountries: phoneAvailableCountries }"
          :translations="phoneTextTranslation"
          :default-country-code="phoneCountryCode"
          @update="updatePhone"
        ></vue-phone-number-input>
      </b-form-group>

      <!--BANK REG NUMBER-->
      <b-form-group
        v-if="showBankRegNumber"
        id="bank-reg-number-group"
        :invalid-feedback="invalidBankRegNumber"
        :state="stateBankRegNumber"
        :label="bankRegNumberLabel"
        label-for="bank-reg-number"
      >
        <b-form-input
          id="bank-reg-number"
          v-model="bankRegNumber"
          :state="($v.bankRegNumber.$error) ? false : null"
          :placeholder="bankRegNumberLabel"
        ></b-form-input>
      </b-form-group>

      <!--BANK ACCOUNT-->
      <b-form-group
        v-if="showBankAccount"
        id="bank-account-group"
        :invalid-feedback="invalidBankAccount"
        :state="stateBankAccount"
        :label="$t('form.customer.bank_account')"
        label-for="bank-account"
      >
        <b-form-input
          id="bank-account"
          v-model="bankAccount"
          :state="($v.bankAccount.$error) ? false : null"
          :placeholder="$t('form.customer.bank_account')"
        ></b-form-input>
      </b-form-group>

      <b-button
        :disabled="loading || !canSave"
        type="submit"
        class="mr-4"
        variant="success">
        <b-icon icon="check"></b-icon>
        {{ $t('form.user_settings.button_apply') }}
      </b-button>
      <b-button
        :disabled="this.loading || !canSave"
        @click="getUserData"
        variant="primary">
        <b-icon icon="arrow-repeat"></b-icon>
        {{ $t('form.user_settings.button_reset') }}
      </b-button>
    </b-form>

  </div>
</template>

<script>
  import {
    COUNTRIES_ARRAY_ONLY,
    GB_COUNTRY,
    MIN_USERNAME_LENGTH,
    MAX_USERNAME_LENGTH,
    PHONE_COUNTRY_CODE_MAPPING,
    PHONE_NUMBER_INPUT_TRANSLATIONS,
    webConfig,
  } from '@base/configs'
  import { email, required, minLength, maxLength, numeric } from 'vuelidate/lib/validators'
  import { ibanValidator } from '@base/customValidators'
  import BankAccountFields from '@base/mixins/BankAccountFields.vue'
  import { parsePhoneNumberFromString } from 'libphonenumber-js'
  import { mapState } from 'vuex'

  export default {
    name: 'ChangeUserData',

    mixins: [ BankAccountFields ],

    data () {
      return {
        username: '',
        email: '',
        phoneAvailableCountries: COUNTRIES_ARRAY_ONLY,
        phoneNumberData: {
          formattedNumber: null
        },
        phoneCountryCode: null,
        bankRegNumber: '',
        bankAccount: '',
        loading: false
      }
    },

    created () {
      this.getUserData()
    },

    props: {
      isAdminSettings: {
        type: Boolean,
        default: false
      }
    },

    computed: {
      ...mapState(['user', 'customerData', 'userMarket']),

      showPhone () {
        return Boolean(!this.isAdminSettings && this.userMarket && this.userMarket.customerAccessToPhoneEditing)
      },

      showBankAccount () {
        return Boolean(!this.isAdminSettings && this.userMarket && this.userMarket.customerAccessToBankAccountEditing)
      },

      showBankRegNumber () {
        return this.showBankAccount && this.usesBankRegNumber(this.bankCountryCode, this.bankFormatCode, this.userMarket)
      },

      // A subaccount cannot change its email, but edits the phone and bank details of its main account.
      canSave () {
        return !this.user.isSubaccount || this.showPhone || this.showBankAccount
      },

      bankCountryCode () {
        return this.userMarket.supportCountry && this.userMarket.supportCountry.code
      },

      bankFormatCode () {
        return this.userMarket.bankFileFormat
      },

      bankAccountIsIban () {
        return this.accountIsIban(this.bankCountryCode, this.bankFormatCode, this.userMarket)
      },

      bankFieldsFreeText () {
        return Boolean(this.userMarket.isFlealoverSoloFeature) && !this.bankAccountIsIban
      },

      bankRegNumberLabel () {
        return this.bankCountryCode === GB_COUNTRY
          ? this.$t('form.customer.sort_code')
          : this.$t('form.customer.bank_reg_number')
      },

      phoneTextTranslation () {
        return PHONE_NUMBER_INPUT_TRANSLATIONS[this.$i18n.locale.toUpperCase()]
      },

      phone () {
        return this.phoneNumberData.formattedNumber ? this.phoneNumberData.e164 : ''
      },

      payloads () {
        return {
          username: this.isAdminSettings ? this.username : undefined,
          email: this.user.isSubaccount ? undefined : this.email,
          phone: this.showPhone ? this.phone : undefined,
          bankRegNumber: this.showBankRegNumber ? this.bankRegNumber : undefined,
          bankAccount: this.showBankAccount ? this.bankAccount : undefined,
        }
      },

      hasChanges () {
        return this.email !== this.user.email
          || this.username !== this.user.name
          || (this.showPhone && this.phone !== this.customerData.mainPhone)
          || (this.showBankRegNumber && this.bankRegNumber !== this.customerData.mainBankRegNumber)
          || (this.showBankAccount && this.bankAccount !== this.customerData.mainBankAccount)
      },

      // VALIDATIONS

      hasInvalidPhoneNumber () {
        return Boolean(this.phoneNumberData.formattedNumber) && !this.phoneNumberData.isValid
      },
      stateUsername () {
        return (!this.$v.username.$error)
      },
      invalidUsername () {
        if (this.$v.username.$error) {
          if (!this.$v.username.required)
            return this.$t('validation.required', { field: this.$t('form.user_settings.username') })
          else if (!this.$v.username.minLength || !this.$v.username.maxLength)
            return this.$t('validation.length_min_max', {
              minLength: this.$v.username.$params.minLength.min,
              maxLength: this.$v.username.$params.maxLength.max,
              field: this.$t('form.user_settings.username')
            })
        }
        return null
      },
      stateEmail () {
        return (!this.$v.email.$error)
      },
      invalidEmail () {
        if (this.$v.email.$error) {
          if (!this.$v.email.required)
            return this.$t('validation.required', { field: this.$t('form.user_settings.email') })
          else if (!this.$v.email.email)
            return this.$t('validation.email')
        }
        return null
      },
      stateBankRegNumber () {
        return (!this.$v.bankRegNumber.$error)
      },
      invalidBankRegNumber () {
        if (this.$v.bankRegNumber.$error) {
          if (!this.$v.bankRegNumber.numeric)
            return this.$t('validation.numeric', { field: this.bankRegNumberLabel })
          else if (!this.$v.bankRegNumber.minLength)
            return this.$t('validation.min_length', {
              field: this.bankRegNumberLabel,
              minLength: this.$v.bankRegNumber.$params.minLength.min
            })
          else if (!this.$v.bankRegNumber.maxLength)
            return this.$t('validation.max_length', {
              field: this.bankRegNumberLabel,
              maxLength: this.$v.bankRegNumber.$params.maxLength.max
            })
        }
        return null
      },
      stateBankAccount () {
        return (!this.$v.bankAccount.$error)
      },
      invalidBankAccount () {
        if (this.$v.bankAccount.$error) {
          if (!this.$v.bankAccount.maxLength)
            return this.$t('validation.max_length', {
              field: this.$t('form.customer.bank_account'),
              maxLength: this.$v.bankAccount.$params.maxLength.max
            })
          else if (this.$v.bankAccount.ibanValidator === false)
            return this.$t('validation.iban', { field: this.$t('form.customer.bank_account') })
        }
        return null
      },
    },

    validations () {
      if (this.isAdminSettings) {
        return {
          username: {
            required,
            minLength: minLength(MIN_USERNAME_LENGTH),
            maxLength: maxLength(MAX_USERNAME_LENGTH),
          },
          email: {
            email,
            required
          }
        }
      } else {
        return {
          email: {
            email,
            required
          },
          bankRegNumber: this.showBankRegNumber && !this.bankFieldsFreeText ? {
            numeric,
            minLength: minLength(this.bankRegNumberLengthFor(this.bankCountryCode)),
            maxLength: maxLength(this.bankRegNumberLengthFor(this.bankCountryCode)),
          } : {},
          bankAccount: this.showBankAccount && !this.bankFieldsFreeText ? {
            maxLength: maxLength(this.bankAccountLengthFor(this.bankCountryCode, this.bankFormatCode, this.userMarket.bankAccountFormat)),
            ...(this.bankAccountIsIban && { ibanValidator }),
          } : {},
        }
      }
    },

    methods: {
      updateUserData () {
        const oldEmail = this.user.email
        this.$v.$touch()
        if (!this.hasChanges) {
          this.$notify({
            group: 'app',
            type: 'warn',
            title: this.$t('notifications.title.warning'),
            text: this.$t('notifications.text.user_settings.warning_user_data_identical')
          })
          return
        }
        if (this.$v.$invalid || this.hasInvalidPhoneNumber)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)
        const action = (this.isAdminSettings) ? this.$_actionTypes.CHANGE_ADMIN_DATA :
          this.$_actionTypes.CHANGE_CUSTOMER_DATA
        this.$store.dispatch(action, this.payloads)
          .then(() => {
            // If new user email differs from initial (case-insensitive), logout them, else just send OK notification
            if (this.email.localeCompare(oldEmail, undefined, {sensitivity: 'base'})) {
              this.$swal({
                title: this.$t('notifications.text.user_settings.warning_verify_email'),
                type: 'success',
              })
                .then((result) => {
                  if (result) {
                    if (this.isAdminSettings) {
                      this.$store.dispatch(this.$_actionTypes.LOGOUT_ADMIN)
                    } else {
                      const urlAfterLogout = `${webConfig.baseURL}/${this.$store.state.marketId}`
                      this.$store.dispatch(this.$_actionTypes.LOGOUT_CUSTOMER, urlAfterLogout)
                    }
                  }
                })
            } else {
              this.$notify({
                group: 'app',
                type: 'success',
                title: this.$t('notifications.title.success'),
                text: this.$t('notifications.text.user_settings.success_user_updated')
              })
              this.updateUserConstants()
            }
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loading = false
            this.loader.hide()
          })
      },

      getUserData () {
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)
        const action = (this.isAdminSettings) ? this.$_actionTypes.GET_ADMIN_DATA :
          this.$_actionTypes.GET_CUSTOMER_DATA
        this.$store.dispatch(action)
          .then(() => {
            this.updateUserConstants()
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loading = false
            this.loader.hide()
          })
      },

      getPhoneCountryCode (phone) {
        if (phone) {
          try {
            const parsed = parsePhoneNumberFromString(phone)
            if (parsed && parsed.country) return parsed.country
          } catch (e) {
            // An unparsable number falls back to the store's country below.
          }
        }
        return this.bankCountryCode || PHONE_COUNTRY_CODE_MAPPING[this.$i18n.locale.toUpperCase()]
      },

      updatePhone (phoneObj) {
        this.phoneNumberData = phoneObj
      },

      updateUserConstants () {
        this.username = this.user.name
        this.email = this.user.email
        if (!this.isAdminSettings) {
          this.phoneNumberData = {
            formattedNumber: this.customerData.mainPhone || null
          }
          this.phoneCountryCode = this.getPhoneCountryCode(this.customerData.mainPhone)
          this.bankRegNumber = this.customerData.mainBankRegNumber || ''
          this.bankAccount = this.customerData.mainBankAccount || ''
        }
      }
    }
  }
</script>

<style>

</style>
